import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { quoteService } from '@/lib/api/quote.service';
import { handleApiError } from '@/lib/api/client';
import type { Quote, QuoteStatus } from '@/types/quote.types';

const STATUS_COLORS: Record<QuoteStatus, string> = {
  submitted: 'bg-blue-100 text-blue-800',
  viewed: 'bg-amber-100 text-amber-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-600',
  withdrawn: 'bg-gray-100 text-gray-600',
  draft: 'bg-gray-100 text-gray-600',
};

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
};

const formatDate = (value: Date | null | string): string => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

export const CelebrationQuoteComparison: React.FC = () => {
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['quotes']);

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const celebrationIdNum = Number.parseInt(celebrationId || '', 10);

  useEffect(() => {
    if (!Number.isFinite(celebrationIdNum) || celebrationIdNum <= 0) {
      setError(t('quotes:comparison.invalidId'));
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await quoteService.listQuotes(celebrationIdNum);
        setQuotes(data);
      } catch (err: unknown) {
        const msg = handleApiError(err);
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [celebrationIdNum]);

  return (
    <>
      <AppBreadcrumbs
        items={[
          { label: 'Celebrations', to: '/celebrations' },
          { label: 'Celebration', to: `/celebrations/${celebrationId}` },
          { label: 'Quotes' },
        ]}
      />
      <PageContainer>
        <Card>
          <CardHeader>
            <CardTitle>{t('quotes:comparison.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            )}

            {!loading && error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            {!loading && !error && quotes.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('quotes:comparison.noQuotes')}</p>
            )}

            {!loading && !error && quotes.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">{t('quotes:comparison.table.quoteNumber')}</th>
                      <th className="py-2 pr-4 font-medium">{t('quotes:comparison.table.vendorId')}</th>
                      <th className="py-2 pr-4 font-medium">{t('quotes:comparison.table.status')}</th>
                      <th className="py-2 pr-4 font-medium">{t('quotes:comparison.table.total')}</th>
                      <th className="py-2 pr-4 font-medium">{t('quotes:comparison.table.validUntil')}</th>
                      <th className="py-2 font-medium">{t('quotes:comparison.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((quote) => (
                      <tr key={quote.id} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="py-3 pr-4 font-mono">{quote.quote_number}</td>
                        <td className="py-3 pr-4">{quote.vendor_id}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[quote.status]}`}
                          >
                            {quote.status}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          {formatCurrency(quote.total_amount, quote.currency)}
                        </td>
                        <td className="py-3 pr-4">{formatDate(quote.valid_until)}</td>
                        <td className="py-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              navigate(
                                `/celebrations/${celebrationId}/quotes/${quote.id}`,
                              )
                            }
                          >
                            {t('quotes:comparison.viewDetails')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </>
  );
};
